import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { clearAuthData, isAuthenticated } from '../../utils/tokenManager';
import { useTheme } from '../../contexts/ThemeContext';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { useLanguage, SupportedLang } from '../../hooks/useLanguage';
import { useUpdateProfileMutation } from '../../store/api/userApi';
import { useLogoutMutation } from '../../store/api/authApi';
import { toast } from 'sonner';

// Simple SVG Icons
const MenuIcon = () => (
  <svg
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="h-6 w-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const UserIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

const SunIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

interface HeaderProps {
  onMenuToggle?: () => void;
  isMenuOpen?: boolean;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
}

export function Header({ onMenuToggle, isMenuOpen, menuButtonRef }: HeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const auth = isAuthenticated();
  const isHomePage = location.pathname === '/';
  const showMobileNav = !auth;
  const { currentLanguage, changeLanguage } = useLanguage();
  const [updateProfile] = useUpdateProfileMutation();

  const [showLangPicker, setShowLangPicker] = useState(false);
  const LANG_OPTIONS: { code: SupportedLang; label: string; flag: string }[] = [
    { code: 'ar', label: 'العربية', flag: '🇸🇦' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'yo', label: 'Yorùbá', flag: '🇳🇬' },
    { code: 'ha', label: 'Hausa', flag: '🇳🇬' },
    { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'bn', label: 'বাংলা', flag: '🇧🇩' },
  ];
  const changeLang = (next: SupportedLang) => {
    const previous = currentLanguage;
    changeLanguage(next);
    if (!auth) return;
    (async () => {
      try {
        await updateProfile({ language: next }).unwrap();
      } catch {
        changeLanguage(previous);
        toast.error(t('navigation.languageSaveError'));
      }
    })();
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [logout] = useLogoutMutation();

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Always clear local state even if the API call fails
    }
    clearAuthData();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const user = auth
    ? (() => {
        try {
          const stored = localStorage.getItem('user');
          return stored
            ? (JSON.parse(stored) as { firstName?: string; email?: string })
            : null;
        } catch {
          return null;
        }
      })()
    : null;

  return (
    <header
      className={`dx-topbar fixed inset-x-0 top-0 z-50 w-full transition-all duration-300 header-glass ${
        isScrolled ? 'is-scrolled' : ''
      } ${auth ? 'dx-topbar--app' : 'dx-topbar--marketing'}`}
    >
      <div className="container mx-auto px-3 sm:px-4">
        <div className="dx-topbar__inner md:h-18 flex h-14 w-full items-center gap-2">
          {/* Start: menu + logo */}
          <div className="dx-topbar__start flex min-w-0 flex-1 items-center gap-1.5 sm:gap-3">
            {(showMobileNav || (auth && onMenuToggle)) && (
              <button
                type="button"
                ref={auth && onMenuToggle ? menuButtonRef : undefined}
                onClick={showMobileNav ? () => setIsMobileNavOpen((o) => !o) : onMenuToggle}
                className={`dx-topbar__menu rounded-lg p-2 text-neutral-light transition-colors duration-200 hover:bg-neutral-dark/50 hover:text-brand-accent ${showMobileNav ? 'lg:hidden' : ''}`}
                aria-label={
                  showMobileNav
                    ? (isMobileNavOpen ? t('navigation.closeMenu') : t('navigation.menu'))
                    : (isMenuOpen ? t('navigation.closeMenu') : t('navigation.menu'))
                }
                aria-expanded={showMobileNav ? isMobileNavOpen : isMenuOpen}
              >
                {(showMobileNav ? isMobileNavOpen : isMenuOpen) ? <CloseIcon /> : <MenuIcon />}
              </button>
            )}
            <Link
              to={auth ? '/dashboard' : '/'}
              className="group flex min-w-0 items-center gap-2 sm:gap-2.5"
              aria-label="Rocket Proxy Home"
            >
              <img
                src="https://res.cloudinary.com/dwhpimotj/image/upload/v1758543752/logo_pnm9g9.png"
                alt={t('common.logoAlt')}
                className="h-7 w-auto flex-shrink-0 sm:h-8"
              />
              <span className="dx-topbar__brand relative overflow-hidden rounded px-0.5 font-heading text-sm font-bold text-brand-accent sm:text-base md:text-lg">
                <span className="relative z-10">Rocket Proxy</span>
              </span>
            </Link>
          </div>

          {/* Center nav — desktop only */}
          {isHomePage && !auth && (
            <nav className="dx-topbar__nav hidden items-center gap-2 lg:flex">
              <button
                onClick={() => scrollToSection('hero')}
                className="dx-topbar__nav-pill px-3 py-1.5 font-body text-sm text-neutral-light transition-colors duration-200 hover:text-brand-accent"
              >
                {t('home.nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('catalog')}
                className="dx-topbar__nav-pill px-3 py-1.5 font-body text-sm text-neutral-light transition-colors duration-200 hover:text-brand-accent"
              >
                {t('home.nav.services')}
              </button>
            </nav>
          )}

          {/* End: controls — compact on small screens */}
          <div className="dx-topbar__end flex flex-shrink-0 items-center justify-end gap-1 sm:gap-2">
            <div className="dx-topbar__tools flex items-center gap-0.5 rounded-full bg-neutral-light/5 p-0.5 ring-1 ring-neutral-light/10">
              <button
                type="button"
                onClick={toggleTheme}
                className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-light transition-colors duration-200 hover:text-brand-accent"
                aria-label={theme === 'dark' ? t('navigation.switchToLight') : t('navigation.switchToDark')}
              >
                {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              </button>
            </div>
            {!auth && (
              <div className="dx-topbar__lang">
                <LanguageSwitcher />
              </div>
            )}

            {auth ? (
              <div className="dx-user-wrap relative">
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen((o) => !o)}
                  className="dx-topbar__user"
                  aria-label="User menu"
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="dx-topbar__user-icon" aria-hidden="true">
                    <UserIcon />
                  </span>
                  <span className="dx-topbar__username">
                    {user?.firstName || user?.email || 'User'}
                  </span>
                </button>

                {isUserMenuOpen &&
                  typeof document !== 'undefined' &&
                  createPortal(
                    <>
                      <div
                        className="dx-user-backdrop"
                        onClick={() => { setIsUserMenuOpen(false); setShowLangPicker(false); }}
                        aria-hidden="true"
                      />
                      <div className="dx-user-menu" role="menu">
                        <div className="dx-user-menu__head">
                          <span className="dx-user-menu__avatar">
                            {String(user?.firstName || user?.email || 'U').slice(0, 1).toUpperCase()}
                          </span>
                          <div>
                            <strong>{user?.firstName || user?.email || 'User'}</strong>
                            <small>{user?.email || ''}</small>
                          </div>
                        </div>
                        <Link
                          to="/profile"
                          role="menuitem"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="dx-user-menu__item"
                        >
                          <UserIcon />
                          <span>{t('navigation.profile')}</span>
                        </Link>
                        <button
                          type="button"
                          className="dx-user-menu__item"
                          onClick={() => setShowLangPicker((o) => !o)}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="flex flex-1 items-center gap-2">
                            {t('navigation.language')}
                            <em>{currentLanguage.toUpperCase()}</em>
                          </span>
                        </button>
                        {showLangPicker && (
                          <div className="dx-user-menu__langs">
                            {LANG_OPTIONS.map((lang) => (
                              <button
                                key={lang.code}
                                type="button"
                                onClick={() => { changeLang(lang.code); setShowLangPicker(false); setIsUserMenuOpen(false); }}
                                className={`dx-user-menu__lang ${lang.code === currentLanguage ? 'is-on' : ''}`}
                              >
                                <span>{lang.flag}</span>
                                <span>{lang.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <button
                          type="button"
                          className="dx-user-menu__item is-danger"
                          onClick={handleLogout}
                        >
                          <LogoutIcon />
                          <span>{t('navigation.logout')}</span>
                        </button>
                      </div>
                    </>,
                    document.body
                  )}
              </div>
            ) : (
              <div className="dx-topbar__auth flex items-center gap-1.5 sm:gap-2">
                <Link
                  to="/login"
                  className="dx-nav-login rounded-full px-2.5 py-1.5 font-body text-xs sm:text-sm text-neutral-light transition-colors duration-200 hover:text-brand-accent sm:px-3.5"
                >
                  {t('auth.login.title')}
                </Link>
                <Link
                  to="/register"
                  className="dx-nav-cta rounded-full bg-brand-accent px-2.5 py-1.5 font-heading text-xs font-semibold text-white transition-colors duration-200 hover:bg-brand-accent/90 sm:px-4 sm:text-sm"
                >
                  {t('auth.register.title')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile marketing drawer */}
      {showMobileNav &&
        isMobileNavOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div
              className="dx-mnav-backdrop lg:hidden"
              onClick={() => setIsMobileNavOpen(false)}
              aria-hidden="true"
            />
            <div
              className="dx-mnav lg:hidden"
              role="dialog"
              aria-modal="true"
              aria-label={t('navigation.menu')}
            >
              <div className="dx-mnav__head">
                <span className="dx-mnav__brand">Rocket Proxy</span>
                <button
                  type="button"
                  onClick={() => setIsMobileNavOpen(false)}
                  className="rounded-lg p-2 text-neutral-light hover:bg-neutral-dark/10"
                  aria-label={t('navigation.closeMenu')}
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="dx-mnav__body">
                {isHomePage && (
                  <>
                    <button
                      type="button"
                      className="dx-mnav__link"
                      onClick={() => {
                        setIsMobileNavOpen(false);
                        scrollToSection('hero');
                      }}
                    >
                      {t('home.nav.home')}
                    </button>
                    <button
                      type="button"
                      className="dx-mnav__link"
                      onClick={() => {
                        setIsMobileNavOpen(false);
                        scrollToSection('catalog');
                      }}
                    >
                      {t('home.nav.services')}
                    </button>
                  </>
                )}
                <Link
                  to="/login"
                  onClick={() => setIsMobileNavOpen(false)}
                  className="dx-mnav__link"
                >
                  {t('auth.login.title')}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMobileNavOpen(false)}
                  className="dx-mnav__cta"
                >
                  {t('auth.register.title')}
                </Link>
                <div className="dx-mnav__meta">
                  <div className="dx-mnav__row">
                    <span className="font-body text-sm text-neutral-medium">{t('navigation.language')}</span>
                    <LanguageSwitcher />
                  </div>
                  <button
                    type="button"
                    className="dx-mnav__row"
                    onClick={() => toggleTheme()}
                  >
                    <span className="font-body text-sm">
                      {theme === 'dark' ? t('navigation.switchToLight') : t('navigation.switchToDark')}
                    </span>
                    {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

    </header>
  );
}
